// Copyright 2024 - MinIO, Inc. All rights reserved.

import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { ingressRpc } from "../../../api";
import {
  HttpResponse,
  MinioIngress,
  MinioIngressDetails,
} from "../../../api/ingressRpcApi";
import { setErrorSnackBarMessage, setSnackBarMessage } from "../../systemSlice";
import { LabelKeyPair } from "screens/common/types";
import { AsyncResourceRequest } from "../../../common/common.types";

export const transformLabelKeyPairToRecord = (
  labelKeyPairs: LabelKeyPair[],
): Record<string, string> => {
  return labelKeyPairs
    .filter((lkp) => lkp.key !== "")
    .reduce((acc: Record<string, string>, curr: LabelKeyPair) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {});
};
export const addIngressAsync = createAsyncThunk(
  "ingress/addIngressAsync",
  async (
    { siteID, namespace }: { siteID: string; namespace: string },
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;

    const name = state.addIngress.name;
    const labels = state.addIngress.labels;
    const annotations = state.addIngress.annotations;
    const ingressRules = state.addIngress.ingressRules;
    const ingressTLS = state.addIngress.ingressTLS;

    let ingress: MinioIngressDetails = {
      metadata: {
        name,
        labels: transformLabelKeyPairToRecord(labels),
        annotations: transformLabelKeyPairToRecord(annotations),
      },
      spec: {
        rules: ingressRules,
        tls: ingressTLS,
      },
    };

    return ingressRpc.api
      .createIngress(siteID, namespace, {
        ingress: ingress,
      })
      .then((res: HttpResponse<MinioIngress>) => {
        dispatch(setSnackBarMessage("Added Ingress successfully"));
        return res.data;
      })
      .catch((err) => {
        dispatch(setErrorSnackBarMessage(err.error));
        return rejectWithValue(err);
      });
  },
);

export const fetchIngressForEdit = createAsyncThunk(
  "ingress/getIngressAsync",
  async (
    {
      siteID,
      namespace,
      resourceID,
    }: {
      siteID: string;
      namespace: string;
      resourceID: string;
    },
    { getState, rejectWithValue, dispatch },
  ) => {
    return ingressRpc.api
      .getIngress(siteID, namespace, resourceID)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.error(err);
        dispatch(setErrorSnackBarMessage(err.error));
        return rejectWithValue(err);
      });
  },
);

export const updateIngressAsync = createAsyncThunk(
  "ingress/updateIngressAsync",
  async (
    req: AsyncResourceRequest,
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;
    const { name, labels, annotations, ingressRules, ingressTLS } =
      state.addIngress;
    const ingress = {
      metadata: {
        name: name,
        labels: transformLabelKeyPairToRecord(labels),
        annotations: transformLabelKeyPairToRecord(annotations),
      },
      spec: {
        rules: ingressRules,
        tls: ingressTLS,
      },
    };

    return ingressRpc.api
      .updateIngress(req.siteId, req.namespace, req.resourceId, {
        ingress: ingress,
      })
      .then((res) => {
        dispatch(setSnackBarMessage("Updated Ingress successfully"));
        return res.data;
      })
      .catch((err) => {
        console.error(err);
        dispatch(setErrorSnackBarMessage(err.error));
        return rejectWithValue(err.error);
      });
  },
);
